import React from 'react'
import { NameDto } from '@/api/Common/Types'
import {
  FormCheckboxField,
  FormField,
  FormMultiSelectField,
  FormSelectField,
} from '@/components/Form/FormField'
import { LoadingOverlay } from '@/components/LoadingOverlay/LoadingOverlay'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { SwapFreeOptionDto } from 'api/Clients/Types/SwapFreeOptionDto'
import { CountryDto } from 'api/Identity/Types/Country'
import { Form, FormikErrors, FormikProps, withFormik } from 'formik'

export interface AddEditSwapFreeOptionFormValues {
  id: string
  countryId: string
  tickmillCompanyIds: number[]
  green: number
  red: number
  daysTillSuspended: number
  enabled: boolean
}

interface OuterProps {
  isOpen?: boolean
  swapFreeOption?: SwapFreeOptionDto
  tickmillCompanies: NameDto[]
  countries: CountryDto[]
  onClose(): void
  onSubmit: (values: AddEditSwapFreeOptionFormValues) => Promise<void>
}

const AddEditSwapFreeOptionFormUI: React.FC<
  FormikProps<AddEditSwapFreeOptionFormValues> & OuterProps
> = (props) => {
  const {
    swapFreeOption,
    countries,
    errors,
    touched,
    setFieldValue,
    handleSubmit,
    isSubmitting,
    isValid,
    values,
    tickmillCompanies,
  } = props

  const handleSubmitForm = () => {
    handleSubmit()
  }

  return (
    <Dialog open={props.isOpen} onOpenChange={(open) => !open && props.onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {swapFreeOption ? 'Edit Swap Free Option' : 'Add New Swap Free Option'}
          </DialogTitle>
        </DialogHeader>
        <Form>
          <div className='flex flex-col gap-4'>
            {!swapFreeOption && (
              <FormSelectField
                name='countryId'
                label='Country'
                options={countries.map((country) => ({
                  id: country.id,
                  name: country.name ?? '',
                }))}
                showSearch
                error={touched.countryId ? errors.countryId : undefined}
                onChange={(selectedId) => {
                  setFieldValue('countryId', selectedId)
                }}
              />
            )}
            <FormMultiSelectField
              name='tickmillCompanyIds'
              label='Operating Company'
              value={values.tickmillCompanyIds.map((id) => {
                const company = tickmillCompanies.find((c) => c.id === id)

                return {
                  value: id,
                  label: company?.name ?? '',
                }
              })}
              options={tickmillCompanies}
              onChange={(value) => {
                setFieldValue('tickmillCompanyIds', Array.isArray(value) ? value : [])
              }}
            />
            <FormField
              name='green'
              label='Green'
              type='number'
              error={touched.green ? errors.green : undefined}
            />
            <FormField
              name='red'
              label='Red'
              type='number'
              error={touched.red ? errors.red : undefined}
            />
            <FormField
              name='daysTillSuspended'
              label='Days Till Suspended'
              type='number'
              error={touched.daysTillSuspended ? errors.daysTillSuspended : undefined}
            />
            <FormCheckboxField name='enabled' label='Enabled' />
          </div>
        </Form>
        <DialogFooter>
          <Button onClick={handleSubmitForm} disabled={isSubmitting || !isValid} type='button'>
            Save
          </Button>
        </DialogFooter>
        <LoadingOverlay isLoading={isSubmitting} />
      </DialogContent>
    </Dialog>
  )
}

export const AddEditSwapFreeOptionForm = withFormik<OuterProps, AddEditSwapFreeOptionFormValues>({
  mapPropsToValues: ({ swapFreeOption }) => {
    return {
      id: swapFreeOption?.id || '',
      countryId: swapFreeOption?.countryId || '',
      tickmillCompanyIds: swapFreeOption?.tickmillCompanies?.map((c) => c.id) ?? [],
      green: swapFreeOption?.green ?? 0,
      red: swapFreeOption?.red ?? 0,
      daysTillSuspended: swapFreeOption?.daysTillSuspended ?? 0,
      enabled: swapFreeOption?.enabled ?? true,
    }
  },
  validate: (values) => {
    const errors: FormikErrors<AddEditSwapFreeOptionFormValues> = {}

    if (!values.countryId) {
      errors.countryId = 'Country is required'
    }

    if (values.tickmillCompanyIds.length < 1) {
      errors.tickmillCompanyIds = 'Operating Company is required'
    }

    const greenNum = typeof values.green === 'string' ? parseFloat(values.green) : values.green
    if (
      values.green === null ||
      values.green === undefined ||
      (typeof values.green === 'number' && isNaN(values.green))
    ) {
      errors.green = 'Green threshold is required'
    } else if (isNaN(greenNum)) {
      errors.green = 'Green threshold must be a valid number'
    }

    const redNum = typeof values.red === 'string' ? parseFloat(values.red) : values.red
    if (
      values.red === null ||
      values.red === undefined ||
      (typeof values.red === 'number' && isNaN(values.red))
    ) {
      errors.red = 'Red threshold is required'
    } else if (isNaN(redNum)) {
      errors.red = 'Red threshold must be a valid number'
    } else if (!isNaN(greenNum) && redNum >= greenNum) {
      errors.red = 'Red threshold must be less than green threshold'
    }

    const daysNum =
      typeof values.daysTillSuspended === 'string'
        ? parseInt(values.daysTillSuspended, 10)
        : values.daysTillSuspended
    if (
      values.daysTillSuspended === null ||
      values.daysTillSuspended === undefined ||
      (typeof values.daysTillSuspended === 'number' && isNaN(values.daysTillSuspended))
    ) {
      errors.daysTillSuspended = 'Days till suspended is required'
    } else if (isNaN(daysNum)) {
      errors.daysTillSuspended = 'Days till suspended must be a valid number'
    } else if (!Number.isInteger(daysNum)) {
      errors.daysTillSuspended = 'Days till suspended must be an integer'
    } else if (daysNum <= 0) {
      errors.daysTillSuspended = 'Days till suspended must be a positive number'
    }
    return errors
  },
  handleSubmit: async (values, { props, setSubmitting }) => {
    try {
      setSubmitting(true)
      await props.onSubmit(values)
    } finally {
      setSubmitting(false)
    }
  },
  validateOnChange: true,
})(AddEditSwapFreeOptionFormUI)
